import React, { useState } from 'react'
import { cloneDeep, set } from 'lodash'
import PropTypes from 'prop-types'
import Counter from '../Counter'
import './passenger-select.scss'

const setInitialSelections = initialValues =>
  initialValues && Object.keys(initialValues)?.length
    ? Object.entries(initialValues).reduce((obj, [direction, passengers]) => {
        if (passengers && Object.keys(passengers)?.length) {
          Object.entries(passengers).forEach(([key, data]) => {
            if (+data.count) {
              set(obj, [direction, key], data.count)
            }
          })
        }

        return obj
      }, {})
    : {}

const PassengerSelect = ({
  handleSubmit,
  isOneWay,
  maxPassengers = null,
  initialValues,
  submitText,
  renderCtas,
  departurePassengerCodes,
}) => {
  const [selections, setSelections] = useState(
    setInitialSelections(initialValues),
  )

  const submit = () => {
    const result = Object.entries(selections).reduce(
      (obj, [directionKey, counts]) => {
        const directionCounts = Object.entries(counts).reduce(
          (countObj, [passengerCode, count]) => {
            if (count) {
              const matchingPassengerData =
                departurePassengerCodes.find(
                  passengerType => passengerType.code === passengerCode,
                ) || {}

              return {
                ...countObj,
                [passengerCode]: {
                  ...matchingPassengerData,
                  count,
                },
              }
            }
            return countObj
          },
          {},
        )

        return {
          ...obj,
          [directionKey]: directionCounts,
        }
      },
      {},
    )
    handleSubmit(result)
  }
  const totalDepartureGuests = Object.values(selections.departure || {}).reduce(
    (num, passengerCount) => {
      return num + +passengerCount || 0
    },
    0,
  )
  const totalReturnGuests = Object.values(selections.return || {}).reduce(
    (num, passengerCount) => {
      return num + +passengerCount || 0
    },
    0,
  )

  return (
    <div className="passenger-select">
      <div className={`passenger-select__header ${isOneWay ? 'one-way' : ''}`}>
        <p className="outward-label">Outward</p>
        {!isOneWay && <p className="return-label">Return</p>}
      </div>

      {cloneDeep(departurePassengerCodes)
        .sort((one, two) => (one.displayOrder < two.displayOrder ? -1 : 1))
        .filter(passengerType => passengerType.enabled && passengerType.display)
        .map((passengerType, i) => {
          const departureCount =
            selections?.departure?.[passengerType.code] || 0
          const returnCount = selections?.return?.[passengerType.code] || 0
          return (
            <div
              key={passengerType.code}
              className={`passenger-rows ${isOneWay ? 'one-way' : ''} ${
                departurePassengerCodes.length - 1 === i ? 'no-border' : ''
              }`}
            >
              <p className="passenger-description">
                {passengerType.description}
              </p>
              <Counter
                count={departureCount}
                disAbleDecrement={
                  departureCount <= 0 || totalDepartureGuests <= 1
                }
                disableIncrement={
                  maxPassengers && totalDepartureGuests >= maxPassengers
                }
                handleChange={newCount => {
                  const currentSelections = cloneDeep(selections)
                  set(
                    currentSelections,
                    ['departure', passengerType.code],
                    newCount,
                  )
                  // https://verbinteractive.atlassian.net/browse/RED200-841
                  //  updating departure also updates return
                  set(
                    currentSelections,
                    ['return', passengerType.code],
                    newCount,
                  )
                  setSelections(currentSelections)
                }}
              />

              {isOneWay ? null : (
                <Counter
                  count={returnCount}
                  disAbleDecrement={returnCount <= 0 || totalReturnGuests <= 1}
                  disableIncrement={
                    maxPassengers && totalReturnGuests >= maxPassengers
                  }
                  handleChange={newCount => {
                    const currentSelections = cloneDeep(selections)
                    set(
                      currentSelections,
                      ['return', passengerType.code],
                      newCount,
                    )
                    setSelections(currentSelections)
                  }}
                />
              )}
            </div>
          )
        })}
      {renderCtas ? (
        renderCtas({ submit })
      ) : (
        <button
          type="button"
          className="btn-primary"
          onClick={event => {
            event.stopPropagation()
            submit()
          }}
          disabled={!totalDepartureGuests || (!isOneWay && !totalReturnGuests)}
        >
          {submitText || 'Continue to Passenger Information'}
        </button>
      )}
    </div>
  )
}

PassengerSelect.defaultProps = {
  maxPassengers: null,
  submitText: 'Continue to Passenger Information',
  renderCtas: null,
  initialValues: {},
  routes: {},
}
PassengerSelect.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  maxPassengers: PropTypes.number,
  initialValues: PropTypes.shape({}),
  submitText: PropTypes.string,
  renderCtas: PropTypes.func,
}

export default PassengerSelect
